import { Text } from '@clay-run/glaze/text';
import { Paperclip } from '@phosphor-icons/react/dist/icons/Paperclip';
import { Sparkle } from '@phosphor-icons/react/dist/icons/Sparkle';
import { useAgentTurn } from '../api-client';
import type { MetaDraft } from '../draft-fixtures';
import type { AgentTurnResponse } from '../types';
import { ChatThread, useChatSession, type ChatMessage } from './ChatThread';

const OPENING: ChatMessage[] = [
  {
    id: 0,
    role: 'assistant',
    text: "Hi! I'll help you set up a Meta ad for Growth to review. What are you promoting, and what should people do after they see it?",
    choices: null,
  },
];

export default function AgentInterview({
  draft,
  creativeNames,
  onDraftPatch,
  filled,
  imageInputId,
}: {
  draft: MetaDraft;
  creativeNames: string[];
  onDraftPatch: (patch: NonNullable<AgentTurnResponse['draftPatch']>) => void;
  filled: { done: number; total: number };
  imageInputId: string;
}) {
  const turn = useAgentTurn();
  const chat = useChatSession({
    opening: OPENING,
    isPending: turn.isPending,
    request: (messages, onReply) =>
      turn.mutate(
        {
          messages,
          draft: {
            ...draft,
            targetAudienceNames: draft.targets.map(t => t.name),
            creativeNames,
            formDescription: draft.destination === 'lead_form' && draft.leadForm ? draft.leadForm.spec.questionHeadline : null,
          },
        },
        {
          onSuccess: res => {
            if (res.draftPatch) onDraftPatch(res.draftPatch);
            onReply(res.reply, res.choices);
          },
        },
      ),
  });

  return (
    <section
      aria-label="Agent interview"
      className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border-primary bg-bg-primary"
    >
      <header className="flex items-center gap-2.5 border-b border-border-primary px-4 py-3">
        <span className="flex size-7 items-center justify-center rounded-full bg-bg-inverse text-content-inverse">
          <Sparkle size={14} weight="fill" />
        </span>
        <Text weight="semibold">Ad agent</Text>
        <Text size="xs" variant="tertiary">
          {filled.done} of {filled.total} draft fields filled
        </Text>
      </header>
      <ChatThread
        messages={chat.messages}
        isPending={turn.isPending}
        error={turn.error}
        onSend={chat.send}
        onRetry={chat.retry}
        inputId="adcc-reply"
        placeholder="Reply to the agent"
        pendingLabel="The agent is thinking…"
        accessory={
          <label
            htmlFor={imageInputId}
            title="Add the ad image"
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-border-primary hover:bg-bg-primary-hover"
          >
            <Paperclip size={16} />
            <span className="sr-only">Add the ad image</span>
          </label>
        }
      />
    </section>
  );
}
